import React from 'react';

import { Box } from '@provider-portal/design-common/kalep/components/Box/Box';

import { StoreSelectorHeader } from './StoreSelectorHeader';
import { StoreSelectorNoResults } from './StoreSelectorNoResults';
import { StoreSelectorSearch } from './StoreSelectorSearch';
import { Availability, StoreListGroup, StoreSelectorList } from './StoreSelectorStoresList';

const SEARCH_DEBOUNCE_MS = 500;

export interface StoreSelectorContentProps {
  stores: StoreListGroup[];
  storeAvailabilities: Map<number, Availability>;
  storeAvailabilitiesLoading: boolean;
  selectedStoreId?: number;
  showAddVenueButton: boolean;
  onAddVenue?: () => void;
  onSelectStore?: (storeId: number) => void;
  onSearchPerformed?: (searchQuery: string, resultsCount: number, searchCount: number) => void;
}

export function StoreSelectorContent({
  stores,
  storeAvailabilities,
  storeAvailabilitiesLoading,
  selectedStoreId,
  showAddVenueButton,
  onAddVenue,
  onSelectStore,
  onSearchPerformed,
}: StoreSelectorContentProps) {
  const [availableStores, setAvailableStores] = React.useState<StoreListGroup[]>(stores);
  const debounceTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const [currentSearchCount, setCurrentSearchCount] = React.useState(1);
  const [query, setQuery] = React.useState('');
  const handleOnSearch = React.useCallback(
    (search: string) => {
      const lowerCaseSearch = search.toLowerCase().trim();
      setQuery(search);
      if (lowerCaseSearch === '') {
        setCurrentSearchCount((prev) => prev + 1);
      }

      if (!lowerCaseSearch) {
        setAvailableStores(stores);
        // Clear any pending debounced callback.
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }
        // Track that search was cleared.
        return;
      }

      const filteredStores = stores
        .map((group) => ({
          ...group,
          stores: group.stores.filter((store) =>
            store.storeName.toLowerCase().includes(lowerCaseSearch),
          ),
        }))
        .filter((group) => group.stores.length > 0);

      setAvailableStores(filteredStores);

      // Calculate total results
      const resultsCount = filteredStores.reduce((total, group) => total + group.stores.length, 0);

      // Clear any existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Debounce the callback to avoid firing on every keystroke
      debounceTimerRef.current = setTimeout(() => {
        // Calculate the new count

        if (onSearchPerformed) {
          onSearchPerformed(search, resultsCount, currentSearchCount);
        }
      }, SEARCH_DEBOUNCE_MS);
    },
    [stores, onSearchPerformed, currentSearchCount],
  );

  // Cleanup debounce timer on unmount
  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handeAddVenue = React.useCallback(() => {
    if (onAddVenue) {
      onAddVenue();
    }
  }, [onAddVenue]);

  const handleSelectStore = React.useCallback(
    (storeId: number) => {
      if (onSelectStore) {
        onSelectStore(storeId);
      }
    },
    [onSelectStore],
  );

  return (
    <>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          // Use theme color to hide scrolling content
          backgroundColor: (t) => t.palette.background.paper,
          // Add padding that was originally on the outer Box
          padding: '12px 20px 0px 20px',
        }}
      >
        <StoreSelectorHeader onAddVenue={handeAddVenue} showAddVenueButton={showAddVenueButton} />
      </Box>
      <Box sx={{ padding: '0 20px 20px 20px' }}>
        <StoreSelectorSearch onSearch={handleOnSearch} />
        {availableStores.length > 0 ? (
          <StoreSelectorList
            stores={availableStores}
            storeAvailabilities={storeAvailabilities}
            storeAvailabilitiesLoading={storeAvailabilitiesLoading}
            onSelectStore={handleSelectStore}
            selectedStoreId={selectedStoreId}
          />
        ) : (
          <StoreSelectorNoResults query={query} />
        )}
      </Box>
    </>
  );
}
